// ----------------------------------------------------------------------
// <copyright file="Izenda.PropertiesEditor.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var ITEM_TYPE = {
    GROUP: 'GROUP',
    FIELD: 'FIELD'
  };

  var m = require('mobx');

  const {
    isObject,
    isFunction,
    isString,
    isUndefined,
    values,
    omit,
    get
  } = require('lodash/fp');

  function PropertiesEditor() {
    this.definedSchema = {};
    this.source = {};
    this.mappingProps = {};
    this.mappingSource = {};
  }

  /**
   * Build data based on schema defined
   * @returns {observable-object}
   */
  PropertiesEditor.prototype.getDataBySchema = function() {
    var result = this.buildData('', this.definedSchema, {});
    return result;
  };

  /**
   * Build data based on schema defined
   * @param {string} schemaName
   * @param {object} schemaDef
   * @param {object} schemaSource
   * @returns {object|array}
   */
  PropertiesEditor.prototype.buildData = function(schemaName, schemaDef, schemaSource) {
    if (!isObject(schemaDef)) {
      return schemaSource;
    }

    schemaSource = schemaSource || {};
    schemaSource.itemType = isObject(schemaDef.fields) ? ITEM_TYPE.GROUP : ITEM_TYPE.FIELD;

    // get data of item
    this.getItemData(schemaName, schemaDef, schemaSource);

    // if item is group, build data for each field
    for (var fieldName in schemaDef.fields) {
      schemaSource[fieldName] = this.buildData(fieldName, schemaDef.fields[fieldName], {});
    }

    return schemaSource;
  };

  /**
   * Get data of item: value & its properties,
   * Structure: {
   *              value: [VALUE]   get from itemDef.value  or execute itemDef.fnValue
   *              props: [OBJECT]  based on itemDef.props
   *            }
   * @param {string} itemName
   * @param {object} itemDef
   * @param {object} itemSource
   * @returns {object}
   */
  PropertiesEditor.prototype.getItemData = function(itemName, itemDef, itemSource) {
    if (!isObject(itemSource)) {
      return itemSource;
    }

    itemSource = itemSource || {};

    // we should respect the original value
    itemSource.value = this.getValue(itemName, itemDef, itemSource);

    // get properties
    itemSource.props = this.getProps(itemName, itemDef, itemSource);

    return itemSource;
  };

  /**
   * Get value of item based on its definition
   * @param  {string} itemName
   * @param  {object} itemDef
   * @param  {object} itemSource
   * @param  {object} itemData
   * @return {}
   */
  PropertiesEditor.prototype.getValue = function(itemName, itemDef, itemSource) {
    var source = this.source || {};
    var mappingSource = this.mappingSource || {};
    var mappingProps = this.mappingProps || {};

    var value = null;

    // read data from [value]
    value = source[itemName] || itemDef.value;

    // read data from [fnValue]
    if (!itemDef.fnValue) {
      return value;
    }

    // read data by execute function
    var fnValue = itemDef.fnValue;

    if (isString(fnValue)) {
      if (isFunction(source[fnValue])) {
        fnValue = source[fnValue].bind(source);
      } else if (isFunction(mappingSource[fnValue])) {
        fnValue = mappingSource[fnValue];
      } else if (isFunction(mappingProps[fnValue])) {
        fnValue = mappingProps[fnValue];
      }

      return isFunction(fnValue) && fnValue(itemName, itemDef);
    }

    return fnValue;
  };

  /**
   * Get properties of item based on its definition
   * @param  {object} itemDef
   * @param  {object} itemData
   * @return {object}
   */
  PropertiesEditor.prototype.getProps = function(itemName, itemDef, itemSource) {
    var mappingSource = this.mappingSource || {};
    var result = {};
    var itemDefProps = itemDef.props;

    // if props of field is defined as an function
    if (isFunction(itemDefProps)) {
      itemDefProps = itemDefProps.call(null, itemName, itemDef);
    }

    // props is a name of function in mappingProps
    if (isString(itemDefProps) && isFunction(mappingSource[itemDefProps])) {
      itemDefProps = mappingSource[itemDefProps].call(null, itemName, itemDef);
    }

    if (!isObject(itemDefProps)) {
      return result;
    }

    itemDef = $izenda.extend(true, {}, itemDef, { props: itemDefProps });

    // if props of field is defined as an object
    var propKey, propVal;
    for (propKey in itemDefProps) {
      propVal = isString(itemDefProps[propKey])
        ? this.getPropValueByKey(itemName, itemDef, itemSource, propKey)
        : itemDefProps[propKey];
      assignPropValue(propKey, propVal);
    }

    return result;

    function assignPropValue(propKey, propVal) {
      var keyPath = '';

      if (propVal instanceof Promise) {
        keyPath = 'promiseProps';
      } else if (isFunction(propVal)) {
        keyPath = 'fnProps';
        // prevent mobx convert to computed-value
        propVal = m.asReference(propVal);
      } else {
        keyPath = 'values';
      }

      result[keyPath] = result[keyPath] || {};
      result[keyPath][propKey] = propVal;
    }
  };

  /**
   * Get props of item based on props-key
   * @param {type} propsKey
   * @param {type} itemDef
   * @param {type} itemData
   * @param {type} args
   * @returns {type}
   */
  PropertiesEditor.prototype.getPropValueByKey = function(itemName, itemDef, itemSource, propKey) {
    if (!itemDef || !itemDef.props || !itemDef.props[propKey]) {
      return;
    }

    var source = this.source || {};
    var mappingProps = this.mappingProps || {};
    var mappingSource = this.mappingSource || {};

    var propValue = itemDef.props[propKey];

    // if props of field is defined as an function
    if (isFunction(propValue)) {
      return propValue;
    } else if (!isString(propValue)) {
      return propValue;
    } else if (!isUndefined(mappingProps[propValue])) {
      return mappingProps[propValue];
    } else if (isFunction(source[propValue])) {
      // get data from source
      return source[propValue].call(source, itemName, itemDef, propKey);
    }
    if (isFunction(mappingSource[propValue])) {
      // get data from mappingSource
      return mappingSource[propValue].call(null, itemName, itemDef, propKey);
    }

    return propValue;
  };

  /**
   * Observer object, its properties which its type is function will be move to staticProps
   * @param  {object} dataSchema
   * @return {object}
   */
  PropertiesEditor.prototype.observerObject = function(dataSchema) {
    observerDataSchema.call(this, dataSchema);

    return dataSchema;

    function observerDataSchema(obj) {
      if (!isObject(obj) || values(ITEM_TYPE).indexOf(obj.itemType) === -1) {
        return;
      }

      m.extendObservable(obj, omit('props', obj));

      obj.props.values && m.extendObservable(obj.props.values, obj.props.values);
      obj.props.promiseProps && this.observerPromiseProp(obj.props.promiseProps);

      // for fields (if any)
      for (var fieldName in omit(['props', 'value', 'itemType'], obj)) {
        observerDataSchema.call(this, obj[fieldName]);
      }
    }
  };

  /**
   * process properties which return promise
   * @param  {object} sourceDef
   * @param  {object} sourceData
   * @return {object}
   */
  PropertiesEditor.prototype.observerPromiseProp = function(sourceDef, sourceData) {
    var propVal;

    sourceData = sourceData || sourceDef;

    for (var propKey in sourceDef) {
      propVal = sourceDef[propKey];

      if (propVal instanceof Promise) {
        if (propVal.isFulfilled && propVal.isFulfilled()) {
          sourceData[propKey] = propVal.value();
        } else {
          setVal(propKey, propVal);
        }
      } else {
        sourceData[propKey] = propVal;
      }
    }

    return m.extendObservable(sourceData, sourceData);

    function setVal(propKey, propVal) {
      sourceData[propKey] = null;
      propVal.then(function(response) {
        sourceData[propKey] = response;
      });
    }
  };

  /**
   * Update data schemae
   * @param  {object} dataSchema
   * @param  {object} newDataSchema
   * @return {object}
   */
  PropertiesEditor.prototype.updateSchemaData = function(dataSchema, newDataSchema) {
    return m.transaction(
      function() {
        mergeDataSchema.call(this, dataSchema, newDataSchema);
      }.bind(this)
    );

    function mergeDataSchema(dataSchema, newDataSchema) {
      if (!isObject(dataSchema) || values(ITEM_TYPE).indexOf(dataSchema.itemType) === -1) {
        return;
      }

      m.extendObservable(dataSchema, omit('props', newDataSchema));

      newDataSchema.props.values && m.extendObservable(dataSchema.props.values, newDataSchema.props.values);
      newDataSchema.props.promiseProps &&
        this.observerPromiseProp(newDataSchema.props.promiseProps, dataSchema.props.promiseProps);

      // for fields (if any)
      for (var fieldName in omit(['props', 'value', 'itemType'], newDataSchema)) {
        mergeDataSchema.call(this, dataSchema[fieldName], newDataSchema[fieldName]);
      }
    }
  };

  /**
   * Set data schema
   * @param {object} dataSchema
   */
  PropertiesEditor.prototype.setDataSchema = function(dataSchema) {
    this.dataSchema = dataSchema;
  };

  /**
   * update data based on keyPath, only update value of field
   * @param  {any} newVal
   * @param  {string} key
   * @param  {string} keyPath
   * @return {undefined}
   */
  PropertiesEditor.prototype.updateDataSchemaByKey = function(newVal, key, keyPath, ...others) {
    m.transaction(() => {
      this.propertyConfig.onChangeValue &&
        this.propertyConfig.onChangeValue(this.dataSchema, key, keyPath, newVal, ...others);
    });

    // Verify and warn whether changed value has not been assigned to dataSchema
    get([...keyPath, key, 'value'], this.dataSchema) !== newVal &&
      console &&
      console.warn('The changed value has not been assigned to data schema in OnChangeValue handler');
  };

  /**
   * set properties config, these values will be used to build dataSchema
   * @param {object} propertyConfig
   */
  PropertiesEditor.prototype.setPropertyConfig = function(propertyConfig) {
    propertyConfig = propertyConfig || {};

    this.propertyConfig = propertyConfig;

    this.definedSchema = propertyConfig.definedSchema;
    this.source = propertyConfig.source;
    this.mappingProps = propertyConfig.mappingProps;
    this.mappingSource = propertyConfig.mappingSource;
  };

  module.exports = PropertiesEditor;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.PropertiesEditor.js